import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Modal from '../common/Modal';
import useBalance from '../../hooks/useBalance';
import useModal from '../../hooks/useModal';
import useAuth from '../../hooks/useAuth';
import useUnreadCount from '../../hooks/useUnreadCount';
import { useDesignTemplate } from '../../contexts/DesignTemplateContext';
import { useLabels } from '../../contexts/LabelsContext';
import { getLogoUrl, getLogoStyle } from '../../utils/logoHelper';
import api from '../../services/api';
import GameSearchPopup from '../common/GameSearchPopup';   // 전역 게임 검색 팝업(lumina:search 이벤트 수신, SPEC §11.1)
import './TopNavBar.css';

/**
 * 루미나 상단 바 — 블랙 `TopNavBar.jsx` 의 화면 층 신작(로직은 원본 그대로).
 *
 * 블랙 3단(1단 메뉴 / 2단 사용자 정보 바 / 3단 액션 버튼)을 루미나 시안대로 재배치한다.
 *   · 툴바(로그인 시만): 게임머니(스포츠·미니게임 운영 시)·보유금·포인트 + 새로고침, 포인트전환, 쪽지(n), 내정보, 로그아웃
 *   · 차콜 알약: 로고 · 메뉴(홈·스포츠·인플레이[PREMIUM]·카지노·슬롯·미니게임·이벤트·고객센터) · 전역 게임 검색 · 충전신청/환전신청(로그인) 또는 로그인/회원가입(게스트) · 햄버거
 *   · 블랙 3단의 게임규정·배팅내역·경기결과·출석체크는 홈 히어로 안의 액션 줄(HomePage)로, 나머지는 마이페이지 탭·드로어·하단 내비로 승계
 *   · 모바일: 드로어(블랙 드로어 항목 + 미니게임) + 하단 내비(홈·슬롯·카지노·스포츠·충전 / 게스트 로그인, 빈 칸이 있으면 미니게임) — 조사 기반(SPEC §11)
 *   · 페이지 배경(세로 안내선 4개 + 광원 2개)도 여기서 그린다 — App.js 를 건드리지 않고 전 화면에 깔기 위해
 * 데이터·동작(잔액 훅, 미읽음, 파트너페이지 설정, 게임종류 토글, 비로그인 가드, 포인트 전환, 로그아웃 확인)은 블랙과 동일하다.
 */
const TopNavBar = () => {
  const designTemplate = useDesignTemplate();
  const { labels } = useLabels();
  const navigate = useNavigate();
  const location = useLocation();
  const { balance, points, sportsMoney, refreshBalance, refreshPoints, convertPoints } = useBalance();
  const { isOpen: isAlertOpen, modalConfig: alertConfig, showAlert, closeModal: closeAlert } = useModal();

  const { isOpen: isConfirmOpen, modalConfig: confirmConfig, showConfirm, closeModal: closeConfirm } = useModal();
  const { user, logout, isGameTypeEnabled } = useAuth();
  const { unreadCount: messageCount, noticeUnread, inquiryUnread } = useUnreadCount();
  // [2026-11-01] 위쪽 숫자는 쪽지+답변 합계다. 안 읽은 게 답변뿐이면 답변이 있는 고객센터로 보낸다 — 쪽지 화면엔 답변이 없다(ur3 실측)
  const messagesPath = noticeUnread === 0 && inquiryUnread > 0 ? '/mypage/customer-center' : '/mypage/customer';
  const [noticeCount, setNoticeCount] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [partnerPageSettings, setPartnerPageSettings] = useState({ enabled: false, url: '', label: '파트너페이지' });
  const headerRef = useRef(null);
  const siteName = designTemplate?.theme?.site?.title || designTemplate?.user_browser_title || designTemplate?.site_title || 'NEWMOON';
  const [logoBroken, setLogoBroken] = useState(false);   // 로고 이미지 404 시 글자 마크
  const orbGRef = useRef(null);
  const orbBRef = useRef(null);

  // 공지사항 카운트 조회
  useEffect(() => {
    if (!user) return;

    const fetchNoticeCount = async () => {
      try {
        const response = await api.get('/notices/unread-count');
        if (response.data.success) {
          setNoticeCount(response.data.data.unreadCount);
        }
      } catch (error) {
        console.error('공지사항 카운트 조회 실패:', error);
        setNoticeCount(0);
      }
    };

    fetchNoticeCount();

    const handleNoticeUpdate = (event) => {
      console.log('공지사항 업데이트 이벤트:', event.detail);
      fetchNoticeCount();
    };

    window.addEventListener('noticeUpdate', handleNoticeUpdate);
    window.addEventListener('noticeSliderUpdate', handleNoticeUpdate);

    return () => {
      window.removeEventListener('noticeUpdate', handleNoticeUpdate);
      window.removeEventListener('noticeSliderUpdate', handleNoticeUpdate);
    };
  }, [user]);

  // 파트너페이지 설정 조회
  useEffect(() => {
    if (!user) return;

    const fetchPartnerPageSettings = async () => {
      try {
        const response = await api.get('/settings/partner-page-url');
        if (response.data.success) {
          setPartnerPageSettings(response.data.data);
        }
      } catch (error) {
        console.error('파트너페이지 설정 조회 실패:', error);
      }
    };

    fetchPartnerPageSettings();
  }, [user]);

  const handleConvert = () => {
    if (points <= 0) {
      showAlert('전환할 포인트가 없습니다.');
      return;
    }

    showConfirm('포인트 전환 하시겠습니까?', async () => {
      try {
        const result = await convertPoints();
        showAlert(result.message);
      } catch (error) {
        showAlert(error.message || '포인트 전환에 실패했습니다.');
      }
    });
  };

  const handleLogout = () => {
    showConfirm('로그아웃 하시겠습니까?', () => {
      logout();
    });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);

    if (!isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  const closeMobileMenu = () => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      document.body.style.overflow = '';
    }
  };

  // 외부 클릭 시 메뉴 닫기 (루미나 드로어 클래스 기준)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const menuContainer = document.querySelector('.drawer');
      const hamburgerBtn = document.querySelector('.burger');

      if (isMobileMenuOpen &&
          menuContainer &&
          !menuContainer.contains(event.target) &&
          hamburgerBtn &&
          !hamburgerBtn.contains(event.target)) {
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // [루미나] 로그인 여부를 body 클래스로 노출 — 시안 CSS 의 .guest-only / .auth-only 규칙이 드로어·하단 내비 항목을 가른다
  useEffect(() => {
    document.body.classList.toggle('guest', !user);
    return () => { document.body.classList.remove('guest'); };
  }, [user]);

  // [루미나] 고정 헤더 높이를 --hdr 로 노출 — 앱형 화면(.page-app)의 상단 여백이 이 값을 쓴다(툴바 유무에 따라 달라짐)
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => document.documentElement.style.setProperty('--hdr', `${el.offsetHeight}px`);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [user]);

  // [루미나] 배경 광원 — 참고 사이트(microgaming.io) 실제 코드와 같은 동작(2026-09-30 운영자 지시 "광원 문제 해결해").
  //   참고 사이트 코드: mousemove 로 (clientX, clientY) 를 기억하고, 매 프레임 gsap.to([녹색, 파란색], {x: clientX, y: clientY + scrollY,
  //   stagger: .15, ease: 'none'}) — 녹색은 지금 마우스를, 파란색은 0.15초 전 마우스를 따라가고, 매 프레임 겹치는 0.5초 선형 이동이
  //   실제로는 시간 상수 약 0.25초의 지수 접근이 된다(프레임마다 남은 거리의 2×(dt/0.5) 만큼 — 실측: 1,100px 이동 0.75초에 녹색 97%·파란색 85%).
  //   목표가 페이지 좌표(스크롤을 더한 값)라 스크롤하면 광원이 페이지와 함께 밀렸다가 마우스 자리로 돌아온다 — 여기서도 페이지 좌표로 기억해 같게 한다.
  //   마우스를 아직 움직이지 않았거나 마우스가 없는 기기(폰)·움직임 최소화 설정: 종전 스크롤 시차(시안 실측값 — 녹색은 오른쪽 y≈470~780 오르내림,
  //   파란색은 중반에 화면 위로 빠졌다 돌아옴). 화면 이동 때는 따라가던 자리를 그대로 이어 간다(광원이 튀지 않게).
  const orbCtlRef = useRef(null);
  useEffect(() => {
    const g = orbGRef.current, b = orbBRef.current;
    if (!g || !b) return undefined;
    const FOLLOW_TAU_S = 0.25;   // 실효 시간 상수(참고 사이트: 매 프레임 0.5초 선형 이동 겹침)
    const BLUE_DELAY_MS = 150;   // 참고 사이트 stagger .15
    const SETTLE_PX = 0.5;       // 이보다 가까우면 도착으로 보고 프레임 반복을 멈춘다
    const boxW = () => Math.min(window.innerWidth * 0.85, 1632);   // .orb 크기(85vw, 상한 1632)
    // 원형 그라디언트 중심(상자의 49.6%, 47.5% — 시안 실측)을 화면 좌표 (cx, cy)에 둔다
    const put = (el, cx, cy) => { const W = boxW(); el.style.transform = `translate(${(cx - 0.496 * W).toFixed(1)}px,${(cy - 0.475 * W).toFixed(1)}px)`; };
    const st = { following: false, mx: 0, my: 0, hist: [], gp: { x: 0, y: 0 }, bp: { x: 0, y: 0 }, raf: 0, last: 0, scrollTicking: false };
    // 종전 스크롤 시차 — 마지막으로 놓은 화면 좌표를 기억해 두었다가, 마우스를 처음 움직일 때 그 자리에서 출발한다
    const scrollLayout = () => {
      const vw = window.innerWidth;
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const t = Math.min(1, window.scrollY / max);
      const cx = vw * 0.935, baseY = 783 * Math.min(1, window.innerHeight / 900);
      const gy = baseY - 320 * Math.sin(Math.PI * t), by = baseY - 900 * Math.sin(Math.PI * t);
      put(g, cx, gy); put(b, cx, by);
      st.gp = { x: cx, y: gy + window.scrollY }; st.bp = { x: cx, y: by + window.scrollY };
    };
    const frame = (now) => {
      st.raf = 0;
      const dt = st.last ? Math.min(0.1, (now - st.last) / 1000) : 1 / 60;
      st.last = now;
      const sy = window.scrollY;
      const tg = { t: now, x: st.mx, y: st.my + sy };   // 녹색 목표 = 지금 마우스(페이지 좌표)
      st.hist.push(tg);
      while (st.hist.length > 1 && st.hist[1].t <= now - BLUE_DELAY_MS) st.hist.shift();
      const tb = st.hist[0].t <= now - BLUE_DELAY_MS ? st.hist[0] : null;   // 파란색 목표 = 0.15초 전 마우스(아직 없으면 제자리)
      const k = 1 - Math.exp(-dt / FOLLOW_TAU_S);
      st.gp.x += (tg.x - st.gp.x) * k; st.gp.y += (tg.y - st.gp.y) * k;
      if (tb) { st.bp.x += (tb.x - st.bp.x) * k; st.bp.y += (tb.y - st.bp.y) * k; }
      put(g, st.gp.x, st.gp.y - sy); put(b, st.bp.x, st.bp.y - sy);
      const near = (p, q) => Math.abs(p.x - q.x) < SETTLE_PX && Math.abs(p.y - q.y) < SETTLE_PX;
      const settled = tb && tb.x === tg.x && tb.y === tg.y && near(st.gp, tg) && near(st.bp, tb);
      if (!settled) st.raf = requestAnimationFrame(frame);
      else st.last = 0;
    };
    const kick = () => { if (!st.raf) st.raf = requestAnimationFrame(frame); };
    const onMouseMove = (e) => { st.mx = e.clientX; st.my = e.clientY; st.following = true; kick(); };
    const onScroll = () => {
      if (st.following) { kick(); return; }
      if (st.scrollTicking) return;
      st.scrollTicking = true;
      requestAnimationFrame(() => { scrollLayout(); st.scrollTicking = false; });
    };
    const onResize = () => { if (st.following) kick(); else scrollLayout(); };
    const mq = (q) => (window.matchMedia ? window.matchMedia(q).matches : false);
    const canFollow = mq('(hover: hover) and (pointer: fine)') && !mq('(prefers-reduced-motion: reduce)');
    orbCtlRef.current = { relayout: () => { if (!st.following) scrollLayout(); } };
    scrollLayout();
    if (canFollow) document.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      if (st.raf) cancelAnimationFrame(st.raf);
      document.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      orbCtlRef.current = null;
    };
  }, []);
  // 화면이 바뀌면 문서 높이가 달라지므로 스크롤 시차 자리를 다시 잡는다(마우스를 따라가는 중이면 그대로 이어 간다)
  useEffect(() => { if (orbCtlRef.current) orbCtlRef.current.relayout(); }, [location.pathname]);

  // 화면이 바뀌면 드로어는 닫는다
  useEffect(() => { closeMobileMenu(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [location.pathname, location.search]);

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    if (num === 0) return '0';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue)) return '0';
    return Math.floor(numValue).toLocaleString('ko-KR');
  };

  // [루미나] 전역 게임 검색 — 게임 검색 팝업(components/common/GameSearchPopup)이 이 이벤트를 받아 연다
  const openSearch = () => { window.dispatchEvent(new CustomEvent('lumina:search')); };

  // 현재 화면 표시(메뉴·하단 내비)
  const path = location.pathname;
  const q = new URLSearchParams(location.search);
  const isLive = path === '/sports' && q.get('mode') === 'live';
  const on = (cond) => (cond ? ' on' : '');
  const guardSports = (e) => { if (!user) { e.preventDefault(); navigate('/login'); } };
  /* [2026-10-07 미니게임] 미니게임 메뉴는 운영 게임종류 목록에 미니게임이 있을 때만 보인다.
     목록을 받기 전(불러오는 중·실패)에는 isGameTypeEnabled 가 미니게임만 false 로 답한다(hooks/useAuth.js GAME_TYPE_DEFAULT_WHEN_UNKNOWN —
     기본이 꺼짐이라, 그렇지 않으면 미니게임을 쓰지 않는 대부분의 사이트에서 첫 화면에 메뉴가 잠깐 떴다 사라진다). */
  const showMinigame = isGameTypeEnabled('minigame');
  /* [2026-10-07 미니게임] 하단 내비는 칸이 5개로 고정이다(lumina.css .bnav grid repeat(5,1fr) — 홈·슬롯·카지노·스포츠·충전/로그인).
     슬롯·카지노·스포츠가 모두 보이면 여섯 번째 칸이 줄을 넘어 잘리므로 그때는 드로어에만 두고, 빈 칸이 있을 때(예: 스포츠 미운영)만 하단 내비에도 둔다. */
  const bnavHasRoom = ['slot', 'casino', 'sports'].filter((t) => isGameTypeEnabled(t)).length < 3;

  const RefreshIcon = () => (
    <svg viewBox="0 0 15 15" fill="currentColor"><path d="M7.5 2.5V0L4.375 3.125L7.5 6.25V3.75C9.8125 3.75 11.25 6.25 10 8.125L11.875 10C14.375 6.875 12.5 2.5 7.5 2.5Z"/><path d="M7.5 11.25C5.1875 11.25 3.75 8.75 5 6.875L3.125 5C0.625 8.125 2.5 12.5 7.5 12.5V15L10.625 11.875L7.5 8.75V11.25Z"/></svg>
  );
  const Arrow = () => (
    <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
  );

  return (
    <>
      {/* 페이지 전체 배경: 광원 2개. [2026-11-03] 세로 안내선 4개는 운영자 지시로 제거 — 화면 장식이라 기능·배치와 무관 */}
      <div className="orb g" ref={orbGRef} aria-hidden="true"></div>
      <div className="orb b" ref={orbBRef} aria-hidden="true"></div>

      <header className="site-header" id="siteHeader" ref={headerRef}>
        {/* 툴바 = 블랙 2단(사용자 정보 바) + 3단의 로그아웃. 로그인 상태에서만 */}
        {user && (
          <div className="toolbar" aria-label="내 정보 툴바"><div className="container tb-in">
            {/* [2026-11-03] 아이디(닉네임) — 디자인1·2 툴바에는 처음부터 있었고 이 틀만 빠져 있었다(운영자 지시 "유저페이지 툴바에 아이디닉네임이 나와야"). 긴 이름은 칸 안에서 줄인다 */}
            <span className="tb-user" title={user.nickname ? `${user.username} (${user.nickname})` : user.username}>
              <span className="tb-userid">{user.username}</span>
              {user.nickname && <><span className="sep">(</span><span className="tb-nick">{user.nickname}</span><span className="sep">)</span></>}
            </span>
            <span className="tb-div"></span>
            {/* [2026-10-07 미니게임] 게임머니(members.gameMoney = 보유금 지갑)는 스포츠와 미니게임이 함께 쓴다 — 둘 중 하나라도 운영하면 보인다.
                WHY: 미니게임만 켜고 스포츠를 끈 사이트에서 미니게임 베팅이 이 지갑에서 빠지는데 금액이 보이지 않았다(서버 규칙: 둘 다 꺼져야 숨김·이전). */}
            {(isGameTypeEnabled('sports') || isGameTypeEnabled('minigame')) && (
              <>
                <span className="tb-stat"><span className="lbl">{labels.game_money}</span><span className="sep">(</span><span className="val">{formatNumber(sportsMoney)}원</span><span className="sep">)</span><button className="rf" type="button" onClick={refreshBalance} title="새로고침" aria-label="새로고침"><RefreshIcon /></button></span>
                <span className="tb-div"></span>
              </>
            )}
            <span className="tb-stat"><span className="lbl">{labels.balance}</span><span className="sep">(</span><span className="pre">W</span><span className="val">{formatNumber(balance)}원</span><span className="sep">)</span><button className="rf" type="button" onClick={refreshBalance} title="새로고침" aria-label="새로고침"><RefreshIcon /></button></span>
            <span className="tb-div"></span>
            <span className="tb-stat"><span className="lbl">{labels.point}</span><span className="sep">(</span><span className="pre">P</span><span className="val">{formatNumber(points)}</span><span className="sep">)</span><button className="rf" type="button" onClick={refreshPoints} title="새로고침" aria-label="새로고침"><RefreshIcon /></button></span>
            <span className="tb-div"></span>
            <button className="tb-btn" type="button" onClick={handleConvert}>포인트전환</button>
            <span className="tb-div"></span>
            <Link className="tb-link" to={messagesPath}><span>쪽지</span><span className="sep">(</span><span className="cnt">{messageCount}</span><span className="sep">)</span></Link>
            <span className="tb-div"></span>
            <Link className="tb-link" to="/mypage/info"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>내정보</Link>
            <span className="tb-div"></span>
            <button className="tb-out" type="button" onClick={handleLogout}><svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>로그아웃</button>
          </div></div>
        )}

        <div className="pill-wrap">
          {/* [2026-10-07 미니게임] has-mg = 메뉴에 미니게임이 있어 간격을 줄이는 표시(TopNavBar.css 주석) */}
          <nav className={`pill${showMinigame ? ' has-mg' : ''}`} aria-label="주 메뉴">
            <Link className="logo" to="/">
              {/* 로고 이미지가 없는 디자인(미등록 주소·직접 포트 접근)은 사이트명 글자 마크로 */}
              {getLogoUrl(designTemplate, 'main') && !logoBroken
                ? <img src={getLogoUrl(designTemplate, 'main')} alt="Logo" style={getLogoStyle(designTemplate, 'main')} className="logo-img" onError={() => setLogoBroken(true)} />
                : <><span className="mk">{(siteName || 'NM').slice(0, 2).toUpperCase()}</span><span className="wd">{siteName}</span></>}
            </Link>
            <div className="menu">
              <Link to="/" className={on(path === '/')}>홈</Link>
              {/* [운영사 게임종류 토글] 스포츠 미운영 시 스포츠/인플레이 숨김 · [비로그인 가드] /sports 는 로그인 전제 */}
              {isGameTypeEnabled('sports') && (
                <Link to="/sports" className={on(path === '/sports' && !isLive)} onClick={guardSports}>스포츠</Link>
              )}
              {isGameTypeEnabled('sports') && (
                <Link to="/sports?mode=live" className={'premium' + on(isLive)} onClick={guardSports}><span className="pbadge">PREMIUM</span>인플레이</Link>
              )}
              {isGameTypeEnabled('casino') && (
                <Link to="/casino" className={on(path === '/casino' || path === '/live-casino')}>카지노 <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg></Link>
              )}
              {isGameTypeEnabled('slot') && (
                <Link to="/slot" className={on(path === '/slot')}>슬롯 <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg></Link>
              )}
              {/* [2026-10-07 미니게임] 카지노·슬롯 옆 네 번째 게임종류(운영자 결정) — 누르면 미니게임 목록(/minigame). 표시 조건은 위 showMinigame 주석 */}
              {showMinigame && (
                <Link to="/minigame" className={on(path === '/minigame')}>미니게임 <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg></Link>
              )}
              <Link to="/mypage/event?tab=event" className={on(path === '/mypage/event' && q.get('tab') === 'event')}>이벤트</Link>
              <Link to="/mypage/customer-center" className={on(path === '/mypage/customer-center')}>고객센터</Link>
            </div>
            <div className="hright">
              <button className="hsearch" type="button" aria-label="게임 검색" title="게임 검색" onClick={openSearch}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg></button>
              {user ? (
                <>
                  <Link className="btn-primary dark auth-only" to="/mypage/deposit">충전신청 <Arrow /></Link>
                  <Link className="btn-secondary dark auth-only" to="/mypage/withdrawal">환전신청</Link>
                </>
              ) : (
                <>
                  <Link className="btn-secondary dark guest-only" to="/login">로그인</Link>
                  <Link className="btn-primary dark guest-only" to="/register">회원가입 <Arrow /></Link>
                </>
              )}
              <button className="burger" type="button" aria-label="메뉴 열기" onClick={toggleMobileMenu}><span></span><span></span><span></span></button>
            </div>
          </nav>
        </div>
      </header>

      {/* 모바일 드로어 — 블랙 드로어 항목·조건 그대로 */}
      {isMobileMenuOpen && <div className="drawer-ov" onClick={closeMobileMenu} />}
      {isMobileMenuOpen && (
        <aside className="drawer" aria-label="모바일 메뉴">
          <div className="drawer-h">
            <span className="logo">{getLogoUrl(designTemplate, 'main') ? <img src={getLogoUrl(designTemplate, 'main')} alt="Logo" style={getLogoStyle(designTemplate, 'main')} className="logo-img" /> : <><span className="mk">{(siteName || 'NM').slice(0, 2).toUpperCase()}</span><span className="wd">{siteName}</span></>}</span>
            <button className="drawer-x" type="button" aria-label="닫기" onClick={closeMobileMenu}><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
          </div>
          {!user && (
            <div className="drawer-auth"><Link className="lk-btn ghost" to="/login" onClick={closeMobileMenu}>로그인</Link><Link className="lk-btn mint" to="/register" onClick={closeMobileMenu}>회원가입</Link></div>
          )}
          {user && (
            <div className="drawer-user">
              <span className="drawer-name">{user.username}{user.member_level_name && <em className="lk-badge">{user.member_level_name}</em>}</span>
              <span>{labels.balance} <b>{formatNumber(balance)}원</b></span>
              <span>{labels.point} <b>{formatNumber(points)} P</b></span>
            </div>
          )}
          <ul className="drawer-menu">
            <li><Link to="/" onClick={closeMobileMenu}>홈</Link></li>
            {isGameTypeEnabled('sports') && <li><Link to="/sports" onClick={(e) => { guardSports(e); closeMobileMenu(); }}>스포츠</Link></li>}
            {isGameTypeEnabled('sports') && <li><Link to="/sports?mode=live" onClick={(e) => { guardSports(e); closeMobileMenu(); }}>인플레이</Link></li>}
            {isGameTypeEnabled('casino') && <li><Link to="/casino" onClick={closeMobileMenu}>카지노</Link></li>}
            {isGameTypeEnabled('slot') && <li><Link to="/slot" onClick={closeMobileMenu}>슬롯</Link></li>}
            {/* [2026-10-07 미니게임] 폰 메뉴에도 같은 자리(슬롯 다음) — 알약 메뉴는 1100px 이하에서 숨는다 */}
            {showMinigame && <li><Link to="/minigame" onClick={closeMobileMenu}>미니게임</Link></li>}
          </ul>
          <div className="drawer-sec">My</div>
          <ul className="drawer-menu sub">
            <li><Link to="/mypage/deposit" onClick={closeMobileMenu}>충전신청</Link></li>
            <li><Link to="/mypage/withdrawal" onClick={closeMobileMenu}>환전신청</Link></li>
            <li><Link to="/mypage/event?tab=notice" onClick={closeMobileMenu}>공지사항</Link></li>
            <li><Link to="/mypage/customer-center" onClick={closeMobileMenu}>고객센터{inquiryUnread > 0 && <span className="lk-badge">{inquiryUnread}</span>}</Link></li>
            <li><Link to="/mypage/customer" onClick={closeMobileMenu}>쪽지{noticeUnread > 0 && <span className="lk-badge">{noticeUnread}</span>}</Link></li>
            <li><Link to="/mypage/event?tab=event" onClick={closeMobileMenu}>이벤트</Link></li>
            <li><Link to="/mypage/betting-list" onClick={closeMobileMenu}>배팅내역</Link></li>
            <li><Link to="/mypage/guide" onClick={closeMobileMenu}>게임규정</Link></li>
            {user && isGameTypeEnabled('sports') && <li><Link to="/sports/results" onClick={closeMobileMenu}>경기결과</Link></li>}
            {user && <li><Link to="/mypage/attendance" onClick={closeMobileMenu}>출석체크</Link></li>}
            {user && <li><Link to="/mypage/info" onClick={closeMobileMenu}>내정보</Link></li>}
            {user && partnerPageSettings.enabled && partnerPageSettings.url && partnerPageSettings.hasChildren && (
              <li><a href={partnerPageSettings.url} target="_blank" rel="noopener noreferrer" onClick={closeMobileMenu}>{partnerPageSettings.label || '파트너페이지'}</a></li>
            )}
          </ul>
          {user && (
            <button className="lk-btn ghost block" type="button" onClick={() => { closeMobileMenu(); handleLogout(); }}>로그아웃</button>
          )}
        </aside>
      )}

      {/* 모바일 하단 내비(1100px 이하에서만 표시, CSS) — 조사 기반 SPEC §11.1 */}
      <nav className="bnav" id="bnav" aria-label="하단 메뉴">
        <Link to="/" className={on(path === '/')}><svg viewBox="0 0 24 24"><path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/></svg>홈</Link>
        {isGameTypeEnabled('slot') && <Link to="/slot" className={on(path === '/slot' || path.startsWith('/gameplay'))}><svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="3"/><path d="M7 10v4M12 10v4M17 10v4"/></svg>슬롯</Link>}
        {isGameTypeEnabled('casino') && <Link to="/casino" className={on(path === '/casino' || path === '/live-casino' || path.startsWith('/casino-gameplay'))}><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><path d="M12 3v6M12 15v6M3 12h6M15 12h6"/></svg>카지노</Link>}
        {isGameTypeEnabled('sports') && <Link to="/sports" className={on(path.startsWith('/sports'))} onClick={guardSports}><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 8l3.8 2.8-1.5 4.4h-4.6L8.2 10.8z"/><path d="M12 3v5M4.2 9.5l4 1.3M19.8 9.5l-4 1.3M7 20l2.7-4.8M17 20l-2.7-4.8"/></svg>스포츠</Link>}
        {/* [2026-10-07 미니게임] 빈 칸이 있을 때만(bnavHasRoom 주석) — 아이콘은 다른 칸과 같은 선 아이콘(게임패드) */}
        {showMinigame && bnavHasRoom && <Link to="/minigame" className={on(path === '/minigame')}><svg viewBox="0 0 24 24"><rect x="2.5" y="7" width="19" height="10" rx="5"/><path d="M7 10.5v3M5.5 12h3"/><path d="M15.5 11h.01M17.5 13h.01"/></svg>미니게임</Link>}
        {user
          ? <Link to="/mypage/deposit" className={on(path === '/mypage/deposit')}><svg viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="3"/><path d="M3 10h18M7 15h4"/></svg>충전</Link>
          : <Link to="/login" className={on(path === '/login')}><svg viewBox="0 0 24 24"><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 20c0-3.3 3.6-5 8-5s8 1.7 8 5"/></svg>로그인</Link>}
      </nav>

      {/* 전역 게임 검색 팝업 — 알약의 검색 버튼(lumina:search)으로 연다 */}
      <GameSearchPopup />

      {/* Modals */}
      <Modal
        id="alertModal"
        isOpen={isAlertOpen}
        onClose={closeAlert}
        text={alertConfig.text}
        type="alert"
        logoSrc={getLogoUrl(designTemplate, 'main')}
      />

      <Modal
        id="confirmModal"
        isOpen={isConfirmOpen}
        onClose={closeConfirm}
        text={confirmConfig.text}
        type="confirm"
        onConfirm={confirmConfig.onConfirm}
        logoSrc={getLogoUrl(designTemplate, 'main')}
      />
    </>
  );
};

export default TopNavBar;
